import { Component } from '../Component.js';
import { TEAM_NAMES, BADGES, ROUNDS } from '../../config/content.js';
import { fk, escHtml, licenceTier, scoredProgress, teamTotal, roundTotal, roundMax, teamLabel } from '../../utils/helpers.js';

export class BoardView extends Component {
  constructor(el, { gameStore }) {
    super(el, {});
    this.gameStore = gameStore;
  }

  template() {
    const G = this.gameStore.getState();
    // Only teams that actually registered — an unstarted/partial workshop
    // shouldn't show empty slots for the rest of the static TEAM_NAMES roster.
    const sorted = TEAM_NAMES
      .filter(t => G.teams?.[fk(t)])
      .map(t => ({ name: t, label: teamLabel(t, G.teams[fk(t)]?.displayName), pts: teamTotal(G.scores, fk(t)), badges: G.badges[fk(t)] ?? {} }))
      .sort((a, b) => b.pts - a.pts);

    const finalMax = ROUNDS.reduce((sum, R) => sum + roundMax(R), 0);
    const lowCut   = Math.floor(finalMax / 3);
    const highCut  = Math.floor((2 * finalMax) / 3);

    const header = `
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;flex-wrap:wrap;gap:6px">
        <h2 style="font-size:16px;font-weight:700;color:var(--surface-navy)">Licence Board</h2>
        <div style="display:flex;gap:4px;flex-wrap:wrap;align-items:center" aria-label="Score legend">
          <span class="licence-badge licence-badge--full">Full (${highCut + 1}–${finalMax})</span>
          <span class="licence-badge licence-badge--conditional">Conditional (${lowCut + 1}–${highCut})</span>
          <span class="licence-badge licence-badge--review">Under Review (0–${lowCut})</span>
        </div>
      </div>
    `;

    if (!sorted.some(t => t.pts > 0)) {
      return header + `<div class="card" role="status" aria-live="polite"><p class="muted" style="font-size:12px">No scores yet.</p></div>`;
    }

    const colHdr = `
      <div class="board-row board-row--header" role="row" aria-label="Column headers">
        <span role="columnheader">#</span>
        <span role="columnheader">Team</span>
        <span role="columnheader">R1</span>
        <span role="columnheader">R2</span>
        <span role="columnheader">R3</span>
        <span role="columnheader" style="text-align:right">Total</span>
        <span role="columnheader">Licence / Badges</span>
      </div>`;

    const rows = sorted.map(({ name, label, pts, badges }, i) => {
      const { complete } = scoredProgress(G.scores, fk(name), ROUNDS);
      const lic  = licenceTier(pts, finalMax, complete);
      const rs   = [0,1,2].map(r => {
        const s = G.scores[`${fk(name)}_R${r}`];
        return Array.isArray(s) ? roundTotal(s) : '—';
      });
      const pills = BADGES.filter(b => badges[b.id])
        .map(b => `<span class="badge-pill ${b.cls}" title="${escHtml(b.label)}">${b.icon}</span>`)
        .join('');

      return `
        <div class="board-row board-row--${lic.tier}" role="row">
          <span style="font-weight:700;color:var(--color-muted);font-size:11px" role="cell">${i + 1}</span>
          <span style="font-weight:600;font-size:12px" role="cell">${escHtml(label)}</span>
          ${rs.map(s => `<span style="text-align:center;font-weight:600;font-size:12px;color:var(--surface-navy)" role="cell">${s}</span>`).join('')}
          <span style="font-weight:700;font-size:14px;color:var(--surface-navy);text-align:right" role="cell">${pts}</span>
          <span role="cell"><span class="licence-badge ${lic.cls}">${lic.label}</span>${pills}</span>
        </div>`;
    }).join('');

    return `
      ${header}
      <div role="table" aria-label="Licence board scores">
        ${colHdr}${rows}
      </div>`;
  }
}
